/**
 * Invoicing API Service - RTK Query
 * BuilderBel Construction Project Management System
 */

import { baseApi } from './api';
import type { ApiResponse } from '../types';
import type {
  Invoice,
  InvoiceListParams,
  InvoiceListResponse,
  InvoiceStats,
  AgingSummary,
  CreateInvoiceRequest,
  UpdateInvoiceRequest,
  SendInvoiceRequest,
  RecordPaymentRequest,
  GenerateFromTimesheetRequest,
  VoidCancelRequest,
} from '../types/invoice';

export const invoicingApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    // ============================================================================
    // CRUD
    // ============================================================================

    getInvoices: builder.query<
      ApiResponse<InvoiceListResponse>,
      { projectId: string; params?: InvoiceListParams }
    >({
      query: ({ projectId, params }) => ({
        url: `/projects/${projectId}/invoices`,
        params,
      }),
      providesTags: ['Invoice'],
    }),

    getInvoice: builder.query<ApiResponse<Invoice>, { projectId: string; invoiceId: string }>({
      query: ({ projectId, invoiceId }) => `/projects/${projectId}/invoices/${invoiceId}`,
      providesTags: (_result, _error, { invoiceId }) => [{ type: 'Invoice', id: invoiceId }],
    }),

    createInvoice: builder.mutation<
      ApiResponse<Invoice>,
      { projectId: string; data: CreateInvoiceRequest }
    >({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/invoices`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Invoice'],
    }),

    updateInvoice: builder.mutation<
      ApiResponse<Invoice>,
      { projectId: string; invoiceId: string; data: UpdateInvoiceRequest }
    >({
      query: ({ projectId, invoiceId, data }) => ({
        url: `/projects/${projectId}/invoices/${invoiceId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { invoiceId }) => [
        'Invoice',
        { type: 'Invoice', id: invoiceId },
      ],
    }),

    deleteInvoice: builder.mutation<
      ApiResponse<null>,
      { projectId: string; invoiceId: string }
    >({
      query: ({ projectId, invoiceId }) => ({
        url: `/projects/${projectId}/invoices/${invoiceId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Invoice'],
    }),

    // ============================================================================
    // WORKFLOW
    // ============================================================================

    sendInvoice: builder.mutation<
      ApiResponse<Invoice>,
      { projectId: string; invoiceId: string; data: SendInvoiceRequest }
    >({
      query: ({ projectId, invoiceId, data }) => ({
        url: `/projects/${projectId}/invoices/${invoiceId}/send`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { invoiceId }) => [
        'Invoice',
        { type: 'Invoice', id: invoiceId },
      ],
    }),

    recordInvoicePayment: builder.mutation<
      ApiResponse<Invoice>,
      { projectId: string; invoiceId: string; data: RecordPaymentRequest }
    >({
      query: ({ projectId, invoiceId, data }) => ({
        url: `/projects/${projectId}/invoices/${invoiceId}/record-payment`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (_result, _error, { invoiceId }) => [
        'Invoice',
        { type: 'Invoice', id: invoiceId },
      ],
    }),

    voidInvoice: builder.mutation<
      ApiResponse<Invoice>,
      { projectId: string; invoiceId: string; data?: VoidCancelRequest }
    >({
      query: ({ projectId, invoiceId, data }) => ({
        url: `/projects/${projectId}/invoices/${invoiceId}/void`,
        method: 'POST',
        body: data || {},
      }),
      invalidatesTags: (_result, _error, { invoiceId }) => [
        'Invoice',
        { type: 'Invoice', id: invoiceId },
      ],
    }),

    cancelInvoice: builder.mutation<
      ApiResponse<Invoice>,
      { projectId: string; invoiceId: string; data?: VoidCancelRequest }
    >({
      query: ({ projectId, invoiceId, data }) => ({
        url: `/projects/${projectId}/invoices/${invoiceId}/cancel`,
        method: 'POST',
        body: data || {},
      }),
      invalidatesTags: (_result, _error, { invoiceId }) => [
        'Invoice',
        { type: 'Invoice', id: invoiceId },
      ],
    }),

    // ============================================================================
    // T&M GENERATION
    // ============================================================================

    generateInvoiceFromTimesheet: builder.mutation<
      ApiResponse<Invoice>,
      { projectId: string; data: GenerateFromTimesheetRequest }
    >({
      query: ({ projectId, data }) => ({
        url: `/projects/${projectId}/invoices/generate-from-timesheet`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Invoice'],
    }),

    // ============================================================================
    // STATISTICS
    // ============================================================================

    getInvoiceStats: builder.query<ApiResponse<InvoiceStats>, { projectId: string }>({
      query: ({ projectId }) => `/projects/${projectId}/invoices/stats`,
      providesTags: ['Invoice'],
    }),

    getAgingSummary: builder.query<ApiResponse<AgingSummary>, { projectId: string }>({
      query: ({ projectId }) => `/projects/${projectId}/invoices/aging-summary`,
      providesTags: ['Invoice'],
    }),
  }),
});

export const {
  // CRUD
  useGetInvoicesQuery,
  useGetInvoiceQuery,
  useCreateInvoiceMutation,
  useUpdateInvoiceMutation,
  useDeleteInvoiceMutation,
  // Workflow
  useSendInvoiceMutation,
  useRecordInvoicePaymentMutation,
  useVoidInvoiceMutation,
  useCancelInvoiceMutation,
  // T&M generation
  useGenerateInvoiceFromTimesheetMutation,
  // Statistics
  useGetInvoiceStatsQuery,
  useGetAgingSummaryQuery,
} = invoicingApi;

export type { InvoiceListParams } from '../types/invoice';
